'use client';

import { fetchManufacturers } from '@eliadmin/core/src/api/manufacturers/manufacturers-api';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { useDebounce } from '@eliadmin/core/src/hooks/use-debounce';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import {
  Bike,
  Check,
  ChevronDown,
  ChevronRight,
  Search,
  SlidersHorizontal,
  Wrench,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import {
  fetchFitmentCategoryTree,
  fetchFitmentSources,
  fetchFitmentTargets,
  fetchRSMotoMotorcycles,
  type FitmentProduct,
} from '../../api/product-fitments-api';
import { getRSMotoMotorcycleCategoryConfig } from '../../config/rsmoto-fitments';
import { CategorizedFitmentProducts } from './categorized-fitment-products';
import { buildFitmentCategoryIndex, resolveFitmentCategorySelection } from './fitment-categories';
import { FitmentCategorySelect } from './fitment-category-select';
import { FitmentPagination, FitmentVariantSummary, getFitmentVariantLabel } from './fitment-ui';

const MOTORCYCLES_PER_PAGE = 12;
const PARTS_PER_PAGE = 30;
const numberFormatter = new Intl.NumberFormat('sk-SK');

function QueryError({ children, onRetry }: { children: ReactNode; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm sm:flex-row sm:items-center sm:justify-between"
    >
      <div>{children}</div>
      {onRetry && (
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          Skúsiť znova
        </Button>
      )}
    </div>
  );
}

function StepHeading({
  id,
  step,
  title,
  description,
  icon,
  headingRef,
}: {
  id: string;
  step: number;
  title: string;
  description: string;
  icon: ReactNode;
  headingRef?: Ref<HTMLHeadingElement>;
}) {
  return (
    <div className="flex items-start gap-3 sm:gap-4">
      <div
        className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
        aria-hidden="true"
      >
        {icon}
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">Krok {step}</p>
        <h2
          ref={headingRef}
          id={id}
          tabIndex={-1}
          className="scroll-mt-6 text-xl font-semibold outline-none sm:text-2xl"
        >
          {title}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function MotorcycleResultCard({
  motorcycle,
  onSelect,
}: {
  motorcycle: FitmentProduct;
  onSelect: () => void;
}) {
  const manufacturerNames = motorcycle.manufacturers?.map(item => item.name).join(', ');
  const code = motorcycle.code || motorcycle.ext_code || `#${motorcycle.id}`;

  return (
    <button
      type="button"
      onClick={onSelect}
      className="group flex min-h-[104px] w-full items-center gap-3 rounded-xl border border-border bg-background p-4 text-left transition-colors hover:border-primary hover:bg-primary/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:gap-4"
    >
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary"
        aria-hidden="true"
      >
        <Bike className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <strong className="line-clamp-2 block text-base leading-snug">{motorcycle.name}</strong>
        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {manufacturerNames && <span>{manufacturerNames}</span>}
          {manufacturerNames && <span aria-hidden="true">·</span>}
          <span className="truncate font-mono">{code}</span>
        </span>
        <span className="mt-2 block">
          <FitmentVariantSummary product={motorcycle} />
        </span>
      </span>
      <ChevronRight
        className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
        aria-hidden="true"
      />
    </button>
  );
}

function SelectedMotorcycleCard({
  motorcycle,
  onChange,
  children,
  headingRef,
}: {
  motorcycle: FitmentProduct;
  onChange: () => void;
  children?: ReactNode;
  headingRef?: Ref<HTMLHeadingElement>;
}) {
  const manufacturerNames = motorcycle.manufacturers?.map(item => item.name).join(', ');
  const code = motorcycle.code || motorcycle.ext_code || `#${motorcycle.id}`;

  return (
    <section aria-label="Vybraná motorka" aria-live="polite" className="rounded-2xl border bg-card">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:p-6">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
          aria-hidden="true"
        >
          <Check className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            Vybraná motorka
          </p>
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="mt-0.5 scroll-mt-6 text-lg font-semibold outline-none sm:text-xl"
          >
            {motorcycle.name}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {[manufacturerNames, code].filter(Boolean).join(' · ')}
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onChange}>
          Zmeniť motorku
        </Button>
      </div>
      {children && <div className="border-t p-4 sm:p-6">{children}</div>}
    </section>
  );
}

export function MotorcycleFitmentFinder() {
  const locale = useLocaleStore(state => state.currentLocale);
  const categoryConfig = getRSMotoMotorcycleCategoryConfig();
  const motorcycleSearchRef = useRef<HTMLInputElement>(null);
  const motorcycleResultsRef = useRef<HTMLDivElement>(null);
  const selectedMotorcycleHeadingRef = useRef<HTMLHeadingElement>(null);
  const partsHeadingRef = useRef<HTMLHeadingElement>(null);
  const [motorcycleListMode, setMotorcycleListMode] = useState<'compatible' | 'all'>('compatible');
  const [motorcycleSearch, setMotorcycleSearch] = useState('');
  const [motorcyclePage, setMotorcyclePage] = useState(1);
  const [manufacturerId, setManufacturerId] = useState<number | null>(null);
  const [selectedMotorcycle, setSelectedMotorcycle] = useState<FitmentProduct | null>(null);
  const [selectedMotorcycleVariantId, setSelectedMotorcycleVariantId] = useState<number | null>(
    null,
  );
  const [partsSearch, setPartsSearch] = useState('');
  const [partsPage, setPartsPage] = useState(1);
  const [selectedPartsCategoryIds, setSelectedPartsCategoryIds] = useState<number[]>([]);
  const debouncedMotorcycleSearch = useDebounce(motorcycleSearch.trim(), 300);
  const debouncedPartsSearch = useDebounce(partsSearch.trim(), 300);

  const scrollToElement = (element: HTMLElement | null) => {
    if (!element) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  };

  const manufacturersQuery = useQuery({
    queryKey: ['rsmoto', 'fitments', 'manufacturers', locale],
    queryFn: () =>
      fetchManufacturers({
        locale,
        query: { results: 100 },
      }),
    enabled: categoryConfig.id !== null,
    staleTime: 5 * 60 * 1000,
  });

  const motorcyclesQuery = useQuery({
    queryKey: [
      'rsmoto',
      'fitments',
      'motorcycles',
      categoryConfig.id,
      motorcycleListMode,
      manufacturerId,
      debouncedMotorcycleSearch,
      motorcyclePage,
      locale,
    ],
    queryFn: ({ signal }) => {
      if (motorcycleListMode === 'all') {
        return fetchRSMotoMotorcycles({
          locale,
          signal,
          query: {
            categoryId: categoryConfig.id!,
            search: debouncedMotorcycleSearch || undefined,
            results: MOTORCYCLES_PER_PAGE,
            page: motorcyclePage,
          },
        });
      }

      return fetchFitmentTargets({
        locale,
        signal,
        query: {
          categoryIds: [categoryConfig.id!],
          manufacturerIds: manufacturerId ? [manufacturerId] : undefined,
          search: debouncedMotorcycleSearch || undefined,
          results: MOTORCYCLES_PER_PAGE,
          page: motorcyclePage,
          includeVariants: true,
        },
      });
    },
    enabled: categoryConfig.id !== null,
  });

  const selectedMotorcycleDetailQuery = useQuery({
    queryKey: ['rsmoto', 'fitments', 'motorcycle-detail', selectedMotorcycle?.slug, locale],
    queryFn: () =>
      fetchSlugDetail({ slug: selectedMotorcycle!.slug.split('?')[0], locale }).then(
        response => response?.['App\\Models\\Product'] ?? null,
      ),
    enabled: motorcycleListMode === 'all' && selectedMotorcycle !== null,
  });

  // Cache the complete tree across motorcycle selections; category loading never blocks products.
  const partsCategoryTreeQuery = useQuery({
    queryKey: ['rsmoto', 'fitments', 'complete-category-tree', locale],
    queryFn: ({ signal }) => fetchFitmentCategoryTree({ locale, signal }),
    enabled: selectedMotorcycle !== null,
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const partsCategoryIndex = useMemo(
    () => buildFitmentCategoryIndex(partsCategoryTreeQuery.data?.data),
    [partsCategoryTreeQuery.data],
  );
  const partsCategoryFilter = useMemo(
    () => resolveFitmentCategorySelection(selectedPartsCategoryIds, partsCategoryIndex),
    [selectedPartsCategoryIds, partsCategoryIndex],
  );
  const hasPartsCategoryFilter = selectedPartsCategoryIds.length > 0;

  useEffect(() => {
    if (!partsCategoryTreeQuery.data || selectedPartsCategoryIds.length === 0) return;
    const existingIds = selectedPartsCategoryIds.filter(id => partsCategoryIndex.byId.has(id));
    if (existingIds.length !== selectedPartsCategoryIds.length) {
      // Reconcile removed categories after a refreshed import, keeping every surviving choice.
      setSelectedPartsCategoryIds(existingIds);
      setPartsPage(1);
    }
  }, [partsCategoryTreeQuery.data, partsCategoryIndex, selectedPartsCategoryIds]);

  const partsQuery = useQuery({
    queryKey: [
      'rsmoto',
      'fitments',
      'categorized-parts',
      selectedMotorcycle?.id,
      selectedMotorcycleVariantId,
      partsCategoryFilter.status,
      partsCategoryFilter.categoryIds,
      debouncedPartsSearch,
      partsPage,
      locale,
    ],
    queryFn: ({ signal }) =>
      fetchFitmentSources({
        locale,
        signal,
        query: {
          targetProductId: selectedMotorcycle!.id,
          targetVariantId: selectedMotorcycleVariantId ?? undefined,
          categoryIds: hasPartsCategoryFilter ? partsCategoryFilter.categoryIds : undefined,
          includeCategories: true,
          search: debouncedPartsSearch || undefined,
          results: PARTS_PER_PAGE,
          page: partsPage,
          includeVariants: true,
        },
      }),
    enabled: selectedMotorcycle !== null && partsCategoryFilter.status === 'ready',
  });

  const resetParts = () => {
    setSelectedMotorcycleVariantId(null);
    setSelectedPartsCategoryIds([]);
    setPartsSearch('');
    setPartsPage(1);
  };

  const selectMotorcycle = (motorcycle: FitmentProduct) => {
    setSelectedMotorcycle(motorcycle);
    resetParts();
    requestAnimationFrame(() => {
      selectedMotorcycleHeadingRef.current?.focus({ preventScroll: true });
      scrollToElement(selectedMotorcycleHeadingRef.current);
    });
  };

  const changeMotorcycle = () => {
    setSelectedMotorcycle(null);
    resetParts();
    requestAnimationFrame(() => {
      motorcycleSearchRef.current?.focus({ preventScroll: true });
      scrollToElement(motorcycleSearchRef.current);
    });
  };

  const changeMotorcycleListMode = (mode: 'compatible' | 'all') => {
    setMotorcycleListMode(mode);
    setManufacturerId(null);
    setMotorcyclePage(1);
  };

  const clearMotorcycleFilters = () => {
    setMotorcycleSearch('');
    setManufacturerId(null);
    setMotorcyclePage(1);
    motorcycleSearchRef.current?.focus();
  };

  const changeMotorcyclePage = (page: number) => {
    setMotorcyclePage(page);
    requestAnimationFrame(() => scrollToElement(motorcycleResultsRef.current));
  };

  const changePartsPage = (page: number) => {
    setPartsPage(page);
    requestAnimationFrame(() => scrollToElement(partsHeadingRef.current));
  };

  const changePartsCategories = (categoryIds: number[]) => {
    setSelectedPartsCategoryIds(categoryIds);
    setPartsPage(1);
  };

  const fitmentVariants =
    selectedMotorcycle?.variant_fitments_differ &&
    (selectedMotorcycle.fitment_variants?.length ?? 0) > 1
      ? (selectedMotorcycle.fitment_variants ?? [])
      : [];
  const detailVariants =
    motorcycleListMode === 'all'
      ? (selectedMotorcycleDetailQuery.data?.variants ?? []).map(variant => ({
          id: variant.id,
          name: variant.name,
          code: variant.code,
          ext_code: (variant as { ext_code?: string | null }).ext_code,
          values: variant.values,
        }))
      : [];
  const selectableMotorcycleVariants =
    fitmentVariants.length > 1 ? fitmentVariants : detailVariants.length > 1 ? detailVariants : [];
  const showMotorcycleVariantPanel =
    selectableMotorcycleVariants.length > 1 ||
    (motorcycleListMode === 'all' &&
      (selectedMotorcycleDetailQuery.isLoading || selectedMotorcycleDetailQuery.isError));
  const sortedManufacturers = [...(manufacturersQuery.data?.data ?? [])].sort((first, second) =>
    first.name.localeCompare(second.name, 'sk'),
  );

  if (categoryConfig.error) {
    return (
      <QueryError>
        <strong>Vyhľadávanie podľa motorky zatiaľ nie je nakonfigurované.</strong>
        <p className="mt-1">{categoryConfig.error}</p>
      </QueryError>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {!selectedMotorcycle ? (
        <section
          aria-labelledby="motorcycle-picker-heading"
          className="space-y-6 rounded-2xl border bg-card p-4 sm:p-6 lg:p-8"
        >
          <StepHeading
            id="motorcycle-picker-heading"
            step={1}
            title="Vyberte svoju motorku"
            description={
              motorcycleListMode === 'compatible'
                ? 'Vyberte model a zobrazíme iba diely, ktoré naň pasujú. Ak poznáte kód modelu, nájdete ho aj podľa neho.'
                : 'Vyberte motorku z ponuky. Pri modeloch bez evidovanej kompatibility sa nemusia zobraziť žiadne diely.'
            }
            icon={<Bike className="h-5 w-5" />}
          />

          {motorcycleListMode === 'all' && (
            <div className="flex flex-col gap-3 rounded-lg border border-dashed bg-muted/40 p-4 text-sm sm:flex-row sm:items-center sm:justify-between">
              <p>
                Zobrazujete aj motorky, ku ktorým zatiaľ nemusí byť evidovaný kompatibilný diel.
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => changeMotorcycleListMode('compatible')}
              >
                Späť na kompatibilné modely
              </Button>
            </div>
          )}

          <div className="rounded-xl bg-muted/40 p-4 sm:p-5">
            <div
              className={
                motorcycleListMode === 'compatible'
                  ? 'grid gap-4 md:grid-cols-[minmax(0,1fr)_280px]'
                  : 'grid gap-4'
              }
            >
              <div className="space-y-2">
                <Label htmlFor="motorcycle-fitment-search">Model alebo kód motorky</Label>
                <Input
                  ref={motorcycleSearchRef}
                  id="motorcycle-fitment-search"
                  type="search"
                  value={motorcycleSearch}
                  placeholder="Napr. EXC, FE 350 alebo kód modelu"
                  before={<Search className="ml-2 h-4 w-4 text-muted-foreground" />}
                  variant="outlined"
                  onChange={event => {
                    setMotorcycleSearch(event.target.value);
                    setMotorcyclePage(1);
                  }}
                />
              </div>
              {motorcycleListMode === 'compatible' && (
                <div className="space-y-2">
                  <Label htmlFor="motorcycle-manufacturer">Výrobca</Label>
                  <div className="relative">
                    <SlidersHorizontal
                      className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <ChevronDown
                      className="pointer-events-none absolute right-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <select
                      id="motorcycle-manufacturer"
                      className="h-12 w-full appearance-none rounded-lg border border-border bg-background pl-10 pr-9 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      value={manufacturerId ?? ''}
                      onChange={event => {
                        setManufacturerId(event.target.value ? Number(event.target.value) : null);
                        setMotorcyclePage(1);
                      }}
                      disabled={manufacturersQuery.isLoading}
                    >
                      <option value="">Všetci výrobcovia</option>
                      {sortedManufacturers.map(manufacturer => (
                        <option key={manufacturer.id} value={manufacturer.id}>
                          {manufacturer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div ref={motorcycleResultsRef} className="scroll-mt-6 space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-semibold">
                  {motorcycleListMode === 'compatible'
                    ? 'Modely s kompatibilnými dielmi'
                    : 'Motorky v ponuke'}
                </h3>
                <p className="mt-0.5 text-sm text-muted-foreground" aria-live="polite">
                  {motorcyclesQuery.isFetching
                    ? 'Aktualizujem výsledky…'
                    : `Počet výsledkov: ${numberFormatter.format(
                        motorcyclesQuery.data?.meta.total ?? 0,
                      )}`}
                </p>
              </div>
              {motorcycleListMode === 'compatible' && (
                <Button
                  type="button"
                  size="sm"
                  variant="link"
                  className="h-auto justify-start p-0 sm:justify-center"
                  onClick={() => changeMotorcycleListMode('all')}
                >
                  Prejsť na motorky v ponuke
                </Button>
              )}
            </div>

            {motorcyclesQuery.isError && (
              <QueryError onRetry={() => void motorcyclesQuery.refetch()}>
                Motorky sa nepodarilo načítať. Skontrolujte pripojenie a skúste to znova.
              </QueryError>
            )}

            {motorcyclesQuery.isLoading ? (
              <div
                className="grid items-start gap-3 md:grid-cols-2"
                role="status"
                aria-label="Načítavam motorky"
              >
                {Array.from({ length: 6 }, (_, index) => (
                  <div key={index} className="h-[104px] animate-pulse rounded-xl bg-muted" />
                ))}
              </div>
            ) : !motorcyclesQuery.isError && (motorcyclesQuery.data?.data.length ?? 0) === 0 ? (
              <div className="rounded-xl border border-dashed p-8 text-center">
                <Bike className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
                <p className="mt-3 font-medium">Nenašli sme zodpovedajúcu motorku</p>
                <p className="mx-auto mt-1 max-w-lg text-sm text-muted-foreground">
                  Skúste kratší názov modelu, kód bez medzier alebo iného výrobcu.
                </p>
                {(motorcycleSearch || manufacturerId) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-4"
                    onClick={clearMotorcycleFilters}
                  >
                    Zrušiť filtre
                  </Button>
                )}
              </div>
            ) : (
              !motorcyclesQuery.isError && (
                <div className="grid items-start gap-3 md:grid-cols-2">
                  {motorcyclesQuery.data?.data.map(motorcycle => (
                    <MotorcycleResultCard
                      key={motorcycle.id}
                      motorcycle={motorcycle}
                      onSelect={() => selectMotorcycle(motorcycle)}
                    />
                  ))}
                </div>
              )
            )}

            <FitmentPagination
              page={motorcyclePage}
              lastPage={motorcyclesQuery.data?.meta.last_page ?? 1}
              onPageChange={changeMotorcyclePage}
            />
          </div>
        </section>
      ) : (
        <SelectedMotorcycleCard
          motorcycle={selectedMotorcycle}
          onChange={changeMotorcycle}
          headingRef={selectedMotorcycleHeadingRef}
        >
          {showMotorcycleVariantPanel && (
            <>
              {motorcycleListMode === 'all' && selectedMotorcycleDetailQuery.isLoading && (
                <p className="text-sm text-muted-foreground" role="status">
                  Načítavam dostupné verzie motorky…
                </p>
              )}
              {motorcycleListMode === 'all' && selectedMotorcycleDetailQuery.isError && (
                <QueryError onRetry={() => void selectedMotorcycleDetailQuery.refetch()}>
                  Verzie motorky sa nepodarilo načítať. Diely zatiaľ zobrazujeme pre celý model.
                </QueryError>
              )}
              {selectableMotorcycleVariants.length > 1 && (
                <div className="max-w-xl space-y-2">
                  <Label htmlFor="motorcycle-fitment-variant">Verzia motorky (voliteľné)</Label>
                  <select
                    id="motorcycle-fitment-variant"
                    className="h-12 w-full rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    value={selectedMotorcycleVariantId ?? ''}
                    onChange={event => {
                      setSelectedMotorcycleVariantId(
                        event.target.value ? Number(event.target.value) : null,
                      );
                      setPartsPage(1);
                    }}
                  >
                    <option value="">Neviem presnú verziu – zobraziť všetky diely</option>
                    {selectableMotorcycleVariants.map(variant => (
                      <option key={variant.id} value={variant.id}>
                        {getFitmentVariantLabel(variant)}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Ak presnú verziu nepoznáte, nechajte zvolenú prvú možnosť.
                  </p>
                </div>
              )}
            </>
          )}
        </SelectedMotorcycleCard>
      )}

      {selectedMotorcycle && (
        <section aria-labelledby="compatible-parts-heading" className="space-y-6">
          <StepHeading
            id="compatible-parts-heading"
            step={2}
            title="Vyberte doplnky pre svoju motorku"
            description={`Zobrazujeme doplnky pre model ${selectedMotorcycle.name}. Výsledky môžete zúžiť kategóriami, názvom alebo kódom.`}
            icon={<Wrench className="h-5 w-5" />}
            headingRef={partsHeadingRef}
          />

          <div className="grid items-start gap-6 lg:grid-cols-[260px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)] xl:gap-8">
            <aside aria-label="Filtrovanie podľa kategórie" className="min-w-0 lg:sticky lg:top-28">
              <FitmentCategorySelect
                categoryTree={partsCategoryTreeQuery.data?.data}
                value={selectedPartsCategoryIds}
                isLoading={partsCategoryTreeQuery.isLoading}
                isError={partsCategoryTreeQuery.isError}
                onRetry={() => void partsCategoryTreeQuery.refetch()}
                onChange={changePartsCategories}
              />
            </aside>
            <div className="min-w-0 space-y-6">
              <div className="min-w-0 space-y-2 rounded-xl bg-muted/40 p-4 sm:p-5">
                <Label htmlFor="compatible-parts-search">
                  Hľadať medzi kompatibilnými doplnkami
                </Label>
                <Input
                  id="compatible-parts-search"
                  type="search"
                  value={partsSearch}
                  placeholder="Názov alebo kód doplnku"
                  before={<Search className="ml-2 h-4 w-4 text-muted-foreground" />}
                  variant="outlined"
                  onChange={event => {
                    setPartsSearch(event.target.value);
                    setPartsPage(1);
                  }}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-semibold">Kompatibilné doplnky</h3>
                <p className="text-sm text-muted-foreground" aria-live="polite">
                  {partsCategoryFilter.status !== 'ready'
                    ? 'Upravte výber kategórií'
                    : partsQuery.isFetching
                      ? 'Aktualizujem výsledky…'
                      : `Počet výsledkov: ${numberFormatter.format(partsQuery.data?.meta.total ?? 0)}`}
                </p>
              </div>

              {partsQuery.isError && (
                <QueryError onRetry={() => void partsQuery.refetch()}>
                  Kompatibilné doplnky sa nepodarilo načítať. Skontrolujte pripojenie a skúste to
                  znova.
                </QueryError>
              )}

              {partsCategoryFilter.status !== 'ready' ? (
                <QueryError>
                  Vyberte najviac 100 samostatných kategórií alebo ich nadradenú kategóriu. Výsledky
                  sa načítajú po úprave výberu.
                </QueryError>
              ) : partsQuery.isLoading ? (
                <div
                  className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3"
                  role="status"
                  aria-label="Načítavam kompatibilné doplnky"
                >
                  {Array.from({ length: 8 }, (_, index) => (
                    <div key={index} className="aspect-[3/4] animate-pulse rounded-lg bg-muted" />
                  ))}
                </div>
              ) : !partsQuery.isError && (partsQuery.data?.data.length ?? 0) === 0 ? (
                <div className="rounded-xl border border-dashed p-8 text-center">
                  <Wrench className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
                  <p className="mt-3 font-medium">
                    {partsSearch || hasPartsCategoryFilter
                      ? 'Tomuto výberu nezodpovedá žiadny kompatibilný doplnok'
                      : 'Pre túto motorku zatiaľ nemáme evidovaný kompatibilný doplnok'}
                  </p>
                  <p className="mx-auto mt-1 max-w-lg text-sm text-muted-foreground">
                    {partsSearch || hasPartsCategoryFilter
                      ? 'Skúste zmeniť kategóriu alebo vyhľadávanie. Vybraná motorka aj verzia zostanú zachované.'
                      : 'Ak hľadáte originálny diel podľa výkresu, použite OEM katalóg vyššie.'}
                  </p>
                  {(partsSearch || hasPartsCategoryFilter) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-4"
                      onClick={() => {
                        setPartsSearch('');
                        setSelectedPartsCategoryIds([]);
                        setPartsPage(1);
                      }}
                    >
                      Zrušiť filtre doplnkov
                    </Button>
                  )}
                </div>
              ) : (
                !partsQuery.isError && (
                  <CategorizedFitmentProducts
                    products={partsQuery.data?.data ?? []}
                    categoryTree={partsCategoryTreeQuery.data?.data}
                    selectedCategoryIds={selectedPartsCategoryIds}
                  />
                )
              )}

              {partsCategoryFilter.status === 'ready' && (
                <FitmentPagination
                  page={partsPage}
                  lastPage={partsQuery.data?.meta.last_page ?? 1}
                  onPageChange={changePartsPage}
                />
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
